/**
 * The operator's keymap editor (`lib/playout/keymap.ts`): every operator
 * action and adlib item with its keys and where they come from (built in,
 * the show style, the panel, or you), a key added by pressing it, a key
 * removed, a command reset to what it inherits — and every key bound to two
 * commands flagged, with Save held back until none is.
 *
 * What it edits is the user's own layer only; the show style's keymap is
 * the show style's. The draft is this component's state until Save.
 */
import type { Keymap } from '@norna/playout-engine/keymap.js';
import { KeyboardIcon, RotateCcwIcon, TriangleAlertIcon, XIcon } from 'lucide-react';
import { type KeyboardEvent, useState } from 'react';
import { type CommandSource, comboFromPress, type KeymapEntry, type KeymapItem, keymapConflicts, resolveKeymap, type UserKeymap, withOverride } from '../../lib/playout/keymap.js';
import { MAX_KEYS_PER_ACTION } from '@norna/playout-engine/keymap.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';

export type KeymapDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The show style's keymap (from the production's snapshot), or null: the built-in keys. */
  readonly showStyleKeymap: Keymap | null;
  readonly items: readonly KeymapItem[];
  /** The user's saved layer. */
  readonly user: UserKeymap;
  /** Where the user's layer is kept, said under the title ("in this browser"). */
  readonly storageNote: string;
  readonly onSave: (user: UserKeymap) => void;
};

const SOURCE: Readonly<Record<CommandSource, { readonly label: string; readonly variant: 'outline' | 'secondary' | 'standby' }>> = {
  'built-in': { label: 'Built in', variant: 'outline' },
  'show-style': { label: 'Show style', variant: 'secondary' },
  panel: { label: 'Panel', variant: 'secondary' },
  user: { label: 'Yours', variant: 'standby' },
};

type RowProps = {
  readonly entry: KeymapEntry;
  readonly clashes: ReadonlyMap<string, readonly string[]>;
  readonly labelOf: (id: string) => string;
  readonly recording: boolean;
  readonly onRecord: (recording: boolean) => void;
  readonly onChange: (combos: readonly string[] | null) => void;
};

function Row({ entry, clashes, labelOf, recording, onRecord, onChange }: RowProps) {
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (!recording) return;
    // Tab still moves focus (and ends the recording on blur); Escape cancels.
    if (event.key === 'Tab' && !event.ctrlKey && !event.altKey && !event.metaKey) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.key === 'Escape') {
      onRecord(false);
      return;
    }
    const combo = comboFromPress({ key: event.key, code: event.code, shift: event.shiftKey, ctrl: event.ctrlKey, alt: event.altKey, meta: event.metaKey });
    if (combo === null) return;
    onChange([...entry.combos, combo]);
    onRecord(false);
  };
  const others = entry.combos.flatMap((combo) => (clashes.get(combo) ?? []).filter((id) => id !== entry.id).map((id) => ({ combo, id })));
  return (
    <li className="grid gap-1.5 border-b py-2" data-testid="keymap-row" data-command={entry.id} data-conflict={others.length > 0}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{entry.label}</span>
        <Badge variant={SOURCE[entry.source].variant} data-testid="keymap-source">
          {SOURCE[entry.source].label}
        </Badge>
        {entry.source === 'user' ? (
          <Button type="button" variant="ghost" size="sm" aria-label={`${entry.label}: back to ${entry.inherited.length === 0 ? 'no key' : entry.inherited.join(', ')}`} onClick={() => onChange(null)} data-testid="keymap-reset">
            <RotateCcwIcon />
          </Button>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {entry.combos.length === 0 ? <span className="text-xs text-muted-foreground">No key</span> : null}
        {entry.combos.map((combo) => (
          <span key={combo} className="inline-flex items-center gap-0.5 rounded border bg-muted pl-1.5 font-mono text-xs" data-testid="keymap-key">
            {combo}
            <Button type="button" variant="ghost" size="sm" className="h-6 px-1" aria-label={`Remove ${combo} from ${entry.label}`} onClick={() => onChange(entry.combos.filter((c) => c !== combo))}>
              <XIcon />
            </Button>
          </span>
        ))}
        <Button
          type="button"
          variant={recording ? 'next' : 'outline'}
          size="sm"
          className="h-6"
          disabled={!recording && entry.combos.length >= MAX_KEYS_PER_ACTION}
          aria-label={recording ? `Press a key for ${entry.label} (Escape cancels)` : `Add a key to ${entry.label}`}
          onClick={(event) => {
            // Some browsers do not focus a clicked button; the key press must land on it.
            event.currentTarget.focus();
            onRecord(!recording);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => onRecord(false)}
          data-testid="keymap-add"
        >
          <KeyboardIcon />
          {recording ? 'Press a key…' : 'Add key'}
        </Button>
      </div>
      {others.map(({ combo, id }) => (
        <p key={`${combo}:${id}`} className="flex items-center gap-1.5 text-xs text-warning" role="alert" data-testid="keymap-conflict">
          <TriangleAlertIcon className="size-3.5 shrink-0" aria-hidden="true" />
          {combo} is also {labelOf(id)}
        </p>
      ))}
    </li>
  );
}

export function KeymapDialog({ open, onOpenChange, showStyleKeymap, items, user, storageNote, onSave }: KeymapDialogProps) {
  const [draft, setDraft] = useState<UserKeymap>(user);
  const [recording, setRecording] = useState<string | null>(null);
  const entries = resolveKeymap(showStyleKeymap, items, draft);
  const conflicts = keymapConflicts(entries);
  const clashes = new Map(conflicts.map((c) => [c.combo, c.ids]));
  const labels = new Map(entries.map((e) => [e.id, e.group === 'Playout' ? e.label : `${e.group}: ${e.label}`]));
  const labelOf = (id: string): string => labels.get(id) ?? id;
  const groups = [...new Set(entries.map((e) => e.group))];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="keymap-dialog"
        onEscapeKeyDown={(event) => {
          // Escape while recording cancels the recording, not the editor.
          if (recording !== null) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>
            Your keys go on top of the show style’s and the built-in ones. {storageNote} Enter, Space, plain arrows and letters fire only when nothing is focused; F-keys and keys with Ctrl, Alt or
            Meta fire from anywhere.
          </DialogDescription>
        </DialogHeader>
        <div className="grid max-h-96 gap-4 overflow-y-auto pr-1">
          {groups.map((group) => (
            <section key={group} aria-label={group}>
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground">{group}</h3>
              <ul>
                {entries
                  .filter((e) => e.group === group)
                  .map((entry) => (
                    <Row
                      key={entry.id}
                      entry={entry}
                      clashes={clashes}
                      labelOf={labelOf}
                      recording={recording === entry.id}
                      onRecord={(on) => setRecording(on ? entry.id : null)}
                      onChange={(combos) => setDraft(withOverride(draft, entry.id, combos))}
                    />
                  ))}
              </ul>
            </section>
          ))}
        </div>
        {conflicts.length > 0 ? (
          <p className="rounded-md border border-warning bg-warning px-3 py-2 text-sm text-warning-foreground" role="status" data-testid="keymap-conflicts">
            {conflicts.length === 1 ? 'One key is' : `${String(conflicts.length)} keys are`} bound to two commands: {conflicts.map((c) => c.combo).join(', ')}. Only the first would run — change or remove one to save.
          </p>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setDraft({})} disabled={Object.keys(draft).length === 0} data-testid="keymap-reset-all">
            Reset all mine
          </Button>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={() => onSave(draft)} disabled={conflicts.length > 0} data-testid="keymap-save">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
